import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppNotification,
  Ballot,
  BallotSelection,
  BallotWithUser,
  CommunityQuestion,
  Game,
  HomeDashboard,
  PollMovers,
  PollResult,
  PollResultTeam,
  Season,
  SystemSettings,
  TeamSummary,
  VoterProfile,
  VoterStats,
  Week,
  WeekState,
} from '@fanvote25/shared';
import { api } from './api';

/** Query keys in one place so invalidation stays honest. */
export const keys = {
  home: ['home'] as const,
  meta: ['meta'] as const,
  weeks: ['weeks'] as const,
  currentWeek: ['weeks', 'current'] as const,
  teams: ['teams'] as const,
  team: (id: string) => ['teams', id] as const,
  teamResume: (id: string) => ['teams', id, 'resume'] as const,
  games: (filters: Record<string, unknown>) => ['games', filters] as const,
  polls: ['polls'] as const,
  poll: (week: number | 'latest') => ['polls', week] as const,
  pollBallots: (week: number, filters: Record<string, unknown>) =>
    ['polls', week, 'ballots', filters] as const,
  pollAnalytics: (week: number) => ['polls', week, 'analytics'] as const,
  pollCompare: (week: number, from: number) => ['polls', week, 'compare', from] as const,
  ballot: (week?: number) => ['ballot', week ?? 'current'] as const,
  ballotHistory: ['ballot', 'history'] as const,
  voters: (filters: Record<string, unknown>) => ['voters', filters] as const,
  voter: (id: string) => ['voters', id] as const,
  voterBallots: (id: string) => ['voters', id, 'ballots'] as const,
  notifications: ['notifications'] as const,
  community: (week?: number) => ['community', week ?? 'current'] as const,
  admin: {
    dashboard: ['admin', 'dashboard'] as const,
    weeks: ['admin', 'weeks'] as const,
    ballots: (filters: Record<string, unknown>) => ['admin', 'ballots', filters] as const,
    voters: (search: string) => ['admin', 'voters', search] as const,
    settings: ['admin', 'settings'] as const,
    audit: ['admin', 'audit'] as const,
    sports: ['admin', 'sports'] as const,
    integrity: (week?: number) => ['admin', 'integrity', week ?? 'current'] as const,
    poll: (week: number) => ['admin', 'poll', week] as const,
    teams: ['admin', 'teams'] as const,
  },
};

const MINUTE = 60_000;

/* ------------------------------------------------------------------ */
/* Public reads                                                        */
/* ------------------------------------------------------------------ */

export function useHome() {
  return useQuery({
    queryKey: keys.home,
    queryFn: () => api.get<HomeDashboard>('/home'),
    staleTime: MINUTE,
    refetchInterval: 2 * MINUTE,
  });
}

export interface MetaPayload {
  brand: { name: string; tagline: string; phrases: string[] };
  pointsTable: { rank: number; points: number }[];
  tiebreakers: string[];
  badges: { id: string; name: string; description: string; icon: string; tier: string }[];
  conferences: { id: string; name: string; shortName: string; tier: string }[];
  settings: Partial<SystemSettings> & { brandTagline: string };
}

export function useMeta() {
  return useQuery({
    queryKey: keys.meta,
    queryFn: () => api.get<MetaPayload>('/meta'),
    staleTime: 30 * MINUTE,
  });
}

export function useWeeks() {
  return useQuery({
    queryKey: keys.weeks,
    queryFn: () =>
      api.get<{ seasonId: string; currentWeekNumber: number; weeks: (Week & { state: WeekState })[] }>(
        '/weeks',
      ),
    staleTime: 5 * MINUTE,
  });
}

export function useCurrentWeek() {
  return useQuery({
    queryKey: keys.currentWeek,
    queryFn: () => api.get<{ season: Season; week: Week; state: WeekState }>('/weeks/current'),
    staleTime: MINUTE,
  });
}

export function useTeams() {
  return useQuery({
    queryKey: keys.teams,
    queryFn: () => api.get<{ teams: TeamSummary[] }>('/teams'),
    staleTime: 5 * MINUTE,
    select: (data) => data.teams,
  });
}

export function useTeam(teamId: string | undefined) {
  return useQuery({
    queryKey: keys.team(teamId ?? ''),
    enabled: Boolean(teamId),
    queryFn: () =>
      api.get<{
        team: TeamSummary;
        schedule: Game[];
        rankingHistory: { weekNumber: number; rank: number | null; points: number }[];
      }>(`/teams/${teamId}`),
    staleTime: 5 * MINUTE,
  });
}

export function useTeamResume(teamId: string | undefined) {
  return useQuery({
    queryKey: keys.teamResume(teamId ?? ''),
    enabled: Boolean(teamId),
    queryFn: () =>
      api.get<{
        teamId: string;
        record: TeamSummary['record'] | null;
        lastFive: Game[];
        nextGame: Game | null;
      }>(`/teams/${teamId}/resume`),
    staleTime: 5 * MINUTE,
  });
}

export function useGames(filters: {
  week?: number;
  status?: string;
  conference?: string;
  ranked?: boolean;
}) {
  return useQuery({
    queryKey: keys.games(filters),
    queryFn: () => api.get<{ games: Game[] }>('/games', filters as Record<string, string | number>),
    staleTime: 30_000,
    select: (data) => data.games,
  });
}

export function usePoll(week: number | 'latest') {
  return useQuery({
    queryKey: keys.poll(week),
    queryFn: () =>
      api.get<{ poll: PollResult | null; movers: PollMovers | null }>(
        week === 'latest' ? '/polls/latest' : `/polls/${week}`,
      ),
    staleTime: 2 * MINUTE,
    retry: (count, error) => {
      // A poll that has not been released yet is a 404 by design, not a fault.
      if ((error as { status?: number }).status === 404) return false;
      return count < 2;
    },
  });
}

export function usePollList() {
  return useQuery({
    queryKey: keys.polls,
    queryFn: () =>
      api.get<{
        polls: {
          weekNumber: number;
          releasedAt?: string;
          ballotsCounted: number;
          totalPointsCast: number;
          topTeamId: string | null;
        }[];
      }>('/polls'),
    staleTime: 5 * MINUTE,
    select: (data) => data.polls,
  });
}

export function usePollBallots(week: number, filters: { search?: string; teamId?: string }) {
  return useQuery({
    queryKey: keys.pollBallots(week, filters),
    enabled: week > 0,
    queryFn: () =>
      api.get<{ ballots: BallotWithUser[]; poll: { weekNumber: number; teams: PollResultTeam[] } }>(
        `/polls/${week}/ballots`,
        { ...filters, limit: 120 },
      ),
    staleTime: 2 * MINUTE,
  });
}

export interface WeekAnalyticsPayload {
  weekNumber: number;
  ballotsCounted: number;
  conferences: {
    conferenceId: string;
    conferenceName: string;
    teamsInTop25: number;
    teamsReceivingVotes: number;
    averageRank: number | null;
    totalPoints: number;
    bestTeamId: string | null;
    bestRank: number | null;
  }[];
  debates: {
    teamId: string;
    highestBallotRank: number;
    lowestBallotRank: number | null;
    consensusRank: number | null;
    spread: number;
    unrankedCount: number;
    ballotAppearances: number;
  }[];
  voters: {
    mostConsensus: { userId: string; username: string; score: number } | null;
    mostControversial: { userId: string; username: string; score: number } | null;
    biggestDebate: WeekAnalyticsPayload['debates'][number] | null;
    teamOptimists: { teamId: string; userId: string; username: string; rank: number }[];
    fanBiasInsights: { fanTeamId: string; aboutTeamId: string; text: string }[];
  };
}

export function usePollAnalytics(week: number) {
  return useQuery({
    queryKey: keys.pollAnalytics(week),
    enabled: week > 0,
    queryFn: () => api.get<WeekAnalyticsPayload>(`/polls/${week}/analytics`),
    staleTime: 5 * MINUTE,
    retry: false,
  });
}

export function usePollCompare(week: number, from: number) {
  return useQuery({
    queryKey: keys.pollCompare(week, from),
    enabled: week > 0,
    queryFn: () =>
      api.get<{
        fromWeek: number | null;
        toWeek: number;
        rows: {
          teamId: string;
          rank: number;
          previousRank: number | null;
          movement: number | null;
          isNew: boolean;
        }[];
        droppedOut: { teamId: string; previousRank: number }[];
      }>(`/polls/${week}/compare`, { from }),
    staleTime: 5 * MINUTE,
    retry: false,
  });
}

/* ------------------------------------------------------------------ */
/* Ballot                                                              */
/* ------------------------------------------------------------------ */

export interface BallotPayload {
  season: Season;
  week: Week;
  state: WeekState;
  ballot: Ballot;
  validation: {
    valid: boolean;
    errors: string[];
    rankedCount: number;
    missingRanks: number[];
    duplicateTeamIds: string[];
  };
  pointsTable: { rank: number; points: number }[];
  editable: boolean;
  maxComment: number;
}

export function useBallot(week?: number, enabled = true) {
  return useQuery({
    queryKey: keys.ballot(week),
    enabled,
    queryFn: () => api.get<BallotPayload>('/ballot', week ? { week } : undefined),
    staleTime: 0,
  });
}

export function useSaveBallotDraft(week?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { selections: BallotSelection[]; comment?: string }) =>
      api.put<{ ballot: Ballot; validation: BallotPayload['validation']; savedAt: string }>(
        '/ballot',
        input,
        week ? { week } : undefined,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData<BallotPayload>(keys.ballot(week), (previous) =>
        previous ? { ...previous, ballot: data.ballot, validation: data.validation } : previous,
      );
    },
  });
}

export function useSubmitBallot(week?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { selections: BallotSelection[]; comment?: string }) =>
      api.post<{ ballot: Ballot; isResubmission: boolean; stats: VoterStats }>(
        '/ballot/submit',
        input,
        week ? { week } : undefined,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.ballot(week) });
      void queryClient.invalidateQueries({ queryKey: keys.home });
      void queryClient.invalidateQueries({ queryKey: keys.ballotHistory });
    },
  });
}

export function useStartBallotFrom(week?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (source: 'last-ballot' | 'poll' | 'blank') =>
      api.post<{ ballot: Ballot; validation: BallotPayload['validation'] }>(
        '/ballot/start-from',
        { source },
        week ? { week } : undefined,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData<BallotPayload>(keys.ballot(week), (previous) =>
        previous ? { ...previous, ballot: data.ballot, validation: data.validation } : previous,
      );
    },
  });
}

export interface BallotHistoryEntry {
  weekNumber: number;
  status: string;
  reviewStatus: string;
  submittedAt?: string;
  updatedAt: string;
  version: number;
  rankedCount: number;
  selections: BallotSelection[];
  comment?: string;
  consensusScore: number | null;
  comparison:
    | { teamId: string; voterRank: number; pollRank: number | null; difference: number | null }[]
    | null;
}

export function useBallotHistory(enabled = true) {
  return useQuery({
    queryKey: keys.ballotHistory,
    enabled,
    queryFn: () => api.get<{ seasonId: string; ballots: BallotHistoryEntry[] }>('/ballot/history'),
    staleTime: MINUTE,
  });
}

/* ------------------------------------------------------------------ */
/* Voters                                                              */
/* ------------------------------------------------------------------ */

export function useVoters(filters: { search?: string; sort?: string }) {
  return useQuery({
    queryKey: keys.voters(filters),
    queryFn: () =>
      api.get<{ voters: { user: VoterProfile['user']; stats: VoterStats }[] }>('/voters', filters),
    staleTime: 2 * MINUTE,
    select: (data) => data.voters,
  });
}

export function useVoter(userId: string | undefined) {
  return useQuery({
    queryKey: keys.voter(userId ?? ''),
    enabled: Boolean(userId),
    queryFn: () => api.get<VoterProfile>(`/voters/${userId}`),
    staleTime: 2 * MINUTE,
  });
}

export function useVoterBallots(userId: string | undefined) {
  return useQuery({
    queryKey: keys.voterBallots(userId ?? ''),
    enabled: Boolean(userId),
    queryFn: () =>
      api.get<{
        user: VoterProfile['user'];
        publicBallotsEnabled: boolean;
        ballots: BallotHistoryEntry[];
      }>(`/voters/${userId}/ballots`),
    staleTime: 2 * MINUTE,
  });
}

/* ------------------------------------------------------------------ */
/* Notifications + community                                           */
/* ------------------------------------------------------------------ */

export function useNotifications(enabled: boolean) {
  return useQuery({
    queryKey: keys.notifications,
    enabled,
    queryFn: () => api.get<{ notifications: AppNotification[]; unread: number }>('/notifications'),
    staleTime: MINUTE,
    refetchInterval: 3 * MINUTE,
  });
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId?: string) => api.post('/notifications/read', { notificationId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.notifications }),
  });
}

export function useCommunityQuestions(week?: number) {
  return useQuery({
    queryKey: keys.community(week),
    queryFn: () =>
      api.get<{ weekNumber: number; questions: CommunityQuestion[]; myVotes: Record<string, string> }>(
        '/community/questions',
        week ? { week } : undefined,
      ),
    staleTime: MINUTE,
  });
}

export function useVoteOnQuestion(week?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, optionId }: { questionId: string; optionId: string }) =>
      api.post<{ question: CommunityQuestion; myVote: string }>(
        `/community/questions/${questionId}/vote`,
        { optionId },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.community(week) });
      void queryClient.invalidateQueries({ queryKey: keys.home });
    },
  });
}
